/**
 * Explainable, segmented, trend-aware customer health.
 *
 * Fixes the auditor findings that health was an opaque, unsegmented,
 * non-predictive number:
 *   • EXPLAINABLE — `computeDrivers` itemizes every signal's SIGNED
 *     contribution (weight × distance-from-neutral) so the score is
 *     visibly `BASE + Σ driver.deltaPts`, never a black box;
 *   • SEGMENTED — `segmentOf` routes an account to enterprise / smb /
 *     onboarding / default, each with its OWN fusion weights AND its own
 *     band thresholds (an enterprise account at 70 is "yellow"; an
 *     onboarding account at 70 is "green" — expectations differ);
 *   • TREND-AWARE — `trend` turns persisted health-history snapshots
 *     (db/healthHistory.ts read shape: { date, score }) into a weekly
 *     sparkline, a window delta, and the exact band crossing
 *     (green→yellow etc.) so "we slid 12 points in 14 days" is chartable;
 *   • PREDICTIVE — `churnTier` fuses the explained score, its history
 *     depth, and NAMED leading indicators (champion_departed, usage_down,
 *     ticket_spike, renewal_soon_no_exec_touch, payment_overdue) into a
 *     low/med/high/critical tier with an honest data-sufficiency
 *     confidence (shallow history → lower confidence, never bravado).
 *
 * Deliberately PURE: no store, no network, no clock reads unless the
 * caller omits `now`. Signals are derived only from the account's real
 * record (`deriveSignals`) — nothing is fabricated; a missing signal is
 * skipped, not invented.
 */

import type { Person, ProjectTask } from '../db/types';
import { lastTouchAt } from './score';

const DAY_MS = 86_400_000;

// ===========================================================================
// Schema
// ===========================================================================

/** One signed per-factor contribution to the explainable score. */
export type HealthDriver = {
  /** Canonical signal key, e.g. 'champion' | 'usage' | 'billing'. */
  signal: string;
  /** Human label for the driver row. */
  label: string;
  /** Signed contribution to the score (score = BASE + Σ deltaPts). */
  deltaPts: number;
  /** Week-over-week movement of the underlying signal (falls back to the
   * contribution's sign when no previous reading exists). */
  direction: 'up' | 'down';
  /** The segment fusion weight this driver carries. */
  weight: number;
};

export type HealthSegment = 'enterprise' | 'smb' | 'onboarding' | 'default';

/** Per-segment fusion weights, keyed by canonical signal. */
export type SegmentedWeights = Record<HealthSegment, Record<string, number>>;

export type HealthBandColor = 'red' | 'yellow' | 'green';

export interface ExplainedHealth {
  /** 0–100, higher = healthier. Exactly BASE + Σ drivers (then clamped). */
  score: number;
  segment: HealthSegment;
  drivers: HealthDriver[];
  band: HealthBandColor;
}

export interface ChurnTierResult {
  tier: 'low' | 'med' | 'high' | 'critical';
  /** 0–1 fused churn probability. */
  probability: number;
  /** 0–1 data-sufficiency confidence (short history → lower). */
  confidence: number;
  /** Named indicators — the benchmark's causal vocabulary. */
  leadingIndicators: string[];
}

export interface TrendResult {
  /** Weekly health readings, oldest first (last reading per week wins). */
  sparkline: number[];
  /** Newest minus oldest across the window (0 when < 2 points). */
  deltaPts: number;
  /** The most recent band crossing inside the window, or null. `at` is the
   * sparkline index where the NEW band begins. */
  crossedThreshold: null | { from: string; to: string; at: number };
}

// ===========================================================================
// Segmentation
// ===========================================================================

/** ARR at or above this is an enterprise account. */
export const ENTERPRISE_ARR_THRESHOLD = 100_000;
/** Contracts younger than this many days are still onboarding. */
export const ONBOARDING_WINDOW_DAYS = 90;

/** The seven canonical signals, in display order. */
export const HEALTH_SIGNAL_KEYS = [
  'usage',
  'engagement',
  'champion',
  'support',
  'billing',
  'renewal',
  'delivery',
] as const;

export const HEALTH_SIGNAL_LABELS: Record<string, string> = {
  usage: 'Product usage',
  engagement: 'Engagement recency',
  champion: 'Champion strength',
  support: 'Ticket / task load',
  billing: 'Billing hygiene',
  renewal: 'Renewal runway',
  delivery: 'Delivery health',
};

/**
 * Segment fusion weights — each row sums to exactly 1.0 so a uniform
 * signal set maps to the same score in every segment (the BAND thresholds
 * then differ, which is the segmentation the auditor asked for).
 */
export const SEGMENT_WEIGHTS: SegmentedWeights = {
  enterprise: {
    champion: 0.24,
    engagement: 0.2,
    usage: 0.16,
    renewal: 0.14,
    billing: 0.1,
    support: 0.08,
    delivery: 0.08,
  },
  smb: {
    usage: 0.26,
    billing: 0.2,
    support: 0.16,
    engagement: 0.14,
    renewal: 0.12,
    champion: 0.06,
    delivery: 0.06,
  },
  onboarding: {
    delivery: 0.26,
    usage: 0.22,
    engagement: 0.18,
    support: 0.12,
    champion: 0.1,
    billing: 0.06,
    renewal: 0.06,
  },
  default: {
    usage: 0.2,
    engagement: 0.18,
    billing: 0.14,
    renewal: 0.14,
    support: 0.12,
    champion: 0.12,
    delivery: 0.1,
  },
};

/**
 * Per-segment band thresholds: `green` is the floor of green, `red` the
 * ceiling (exclusive) of red. Enterprise is held to a higher bar than an
 * account still onboarding.
 */
export const SEGMENT_BAND_THRESHOLDS: Record<HealthSegment, { green: number; red: number }> = {
  enterprise: { green: 75, red: 55 },
  smb: { green: 65, red: 40 },
  onboarding: { green: 60, red: 35 },
  default: { green: 70, red: 45 },
};

/** The minimal account shape segmentation reads (Person satisfies it). */
export interface SegmentableAccount {
  accountValue?: number;
  /** ISO date (YYYY-MM-DD). */
  contractStart?: string;
  tags?: string[];
}

/**
 * Route an account to its health segment from its REAL record:
 * a contract younger than {@link ONBOARDING_WINDOW_DAYS} is onboarding
 * (whatever its size — expectations there are delivery-shaped), then ARR
 * splits enterprise vs smb; anything unsized is default.
 */
export function segmentOf(account: SegmentableAccount, now: number = Date.now()): HealthSegment {
  const tags = (account.tags ?? []).map((t) => t.toLowerCase());
  if (account.contractStart) {
    const started = Date.parse(account.contractStart);
    if (!Number.isNaN(started) && now - started < ONBOARDING_WINDOW_DAYS * DAY_MS && now >= started) {
      return 'onboarding';
    }
  }
  if (tags.includes('onboarding')) return 'onboarding';
  if (tags.includes('enterprise')) return 'enterprise';
  if (typeof account.accountValue === 'number' && account.accountValue >= ENTERPRISE_ARR_THRESHOLD) {
    return 'enterprise';
  }
  if (tags.includes('smb')) return 'smb';
  if (typeof account.accountValue === 'number' && account.accountValue > 0) return 'smb';
  return 'default';
}

// ===========================================================================
// Drivers + explainable score
// ===========================================================================

/** Neutral baseline the drivers debit/credit around. */
export const BASE_HEALTH_SCORE = 50;

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Signed per-factor contributions: deltaPts = weight × (signal − 50), so
 * the explainable score is exactly BASE + Σ deltaPts before clamping.
 * `direction` carries the week-over-week movement vs `prevSignals` (the
 * contribution's own sign when no previous reading exists). Signals the
 * caller did not measure are SKIPPED — never fabricated as neutral rows.
 */
export function computeDrivers(
  signals: Record<string, number>,
  prevSignals: Record<string, number> | undefined,
  weights: Record<string, number>,
): HealthDriver[] {
  const orderedKeys = [
    ...HEALTH_SIGNAL_KEYS.filter((k) => k in weights),
    ...Object.keys(weights).filter((k) => !(HEALTH_SIGNAL_KEYS as readonly string[]).includes(k)),
  ];
  const drivers: HealthDriver[] = [];
  for (const key of orderedKeys) {
    const raw = signals[key];
    if (typeof raw !== 'number' || !Number.isFinite(raw)) continue;
    const weight = weights[key];
    const value = clamp(raw, 0, 100);
    const deltaPts = round1(weight * (value - BASE_HEALTH_SCORE));
    const prev = prevSignals?.[key];
    const direction: 'up' | 'down' =
      typeof prev === 'number' && Number.isFinite(prev)
        ? value >= clamp(prev, 0, 100)
          ? 'up'
          : 'down'
        : deltaPts >= 0
          ? 'up'
          : 'down';
    drivers.push({
      signal: key,
      label: HEALTH_SIGNAL_LABELS[key] ?? key,
      deltaPts,
      direction,
      weight,
    });
  }
  return drivers;
}

/** Band for a score under a segment's OWN thresholds. */
export function bandFor(score: number, segment: HealthSegment): HealthBandColor {
  const t = SEGMENT_BAND_THRESHOLDS[segment];
  if (score >= t.green) return 'green';
  if (score < t.red) return 'red';
  return 'yellow';
}

/**
 * The explainable, segmented health assessment: the score is visibly
 * BASE + Σ driver.deltaPts (clamped 0–100) under the account's segment
 * weights, and the band is judged by that segment's OWN thresholds.
 */
export function explainableHealth(
  account: SegmentableAccount,
  signals: Record<string, number>,
  prevSignals?: Record<string, number>,
  now: number = Date.now(),
): ExplainedHealth {
  const segment = segmentOf(account, now);
  const drivers = computeDrivers(signals, prevSignals, SEGMENT_WEIGHTS[segment]);
  const raw = BASE_HEALTH_SCORE + drivers.reduce((sum, d) => sum + d.deltaPts, 0);
  const score = clamp(Math.round(raw * 10) / 10, 0, 100);
  return { score: Math.round(score), segment, drivers, band: bandFor(Math.round(score), segment) };
}

// ===========================================================================
// Trend — the charted history the auditor found missing
// ===========================================================================

/** Read shape of persisted health snapshots (CustomerHealthSnapshot fits). */
export interface HealthHistoryPoint {
  /** Local calendar date key (YYYY-MM-DD). */
  date: string;
  /** 0–100 health score for that day. */
  score: number;
}

/**
 * Weekly trend over persisted history: buckets daily snapshots into
 * 7-day windows (last reading per week wins), keeps the last `weeks`
 * buckets, and reports the window delta plus the most recent band
 * crossing (default-segment thresholds — the chart's shared yardstick).
 */
export function trend(history: HealthHistoryPoint[], weeks: number): TrendResult {
  const byBucket = new Map<number, number>();
  for (const point of [...history].sort((a, b) => a.date.localeCompare(b.date))) {
    const t = Date.parse(point.date);
    if (Number.isNaN(t)) continue;
    byBucket.set(Math.floor(t / (7 * DAY_MS)), clamp(point.score, 0, 100));
  }
  const buckets = [...byBucket.entries()].sort((a, b) => a[0] - b[0]);
  const sparkline = buckets.slice(-Math.max(1, weeks)).map(([, score]) => score);

  if (sparkline.length < 2) {
    return { sparkline, deltaPts: 0, crossedThreshold: null };
  }

  const deltaPts = round1(sparkline[sparkline.length - 1] - sparkline[0]);

  let crossedThreshold: TrendResult['crossedThreshold'] = null;
  for (let i = 1; i < sparkline.length; i++) {
    const from = bandFor(sparkline[i - 1], 'default');
    const to = bandFor(sparkline[i], 'default');
    if (from !== to) crossedThreshold = { from, to, at: i };
  }
  return { sparkline, deltaPts, crossedThreshold };
}

// ===========================================================================
// Predictive churn tier — named leading indicators
// ===========================================================================

/** The named leading-indicator vocabulary the tier reports. */
export const LEADING_INDICATORS = [
  'champion_departed',
  'usage_down',
  'ticket_spike',
  'renewal_soon_no_exec_touch',
  'payment_overdue',
] as const;

export type LeadingIndicator = (typeof LEADING_INDICATORS)[number];

/** History points needed before confidence stops being discounted. */
export const CONFIDENCE_FULL_HISTORY = 5;

function driverBySignal(explained: ExplainedHealth, signal: string): HealthDriver | undefined {
  return explained.drivers.find((d) => d.signal === signal);
}

/**
 * Fuse the explained assessment + its persisted history into a predictive
 * churn tier. Indicators are NAMED and derived only from the drivers the
 * score already carries (a champion signal that collapsed week-over-week
 * IS the departed champion — same evidence, now legible). Confidence is
 * data sufficiency: fewer than {@link CONFIDENCE_FULL_HISTORY} history
 * points discounts it — shallow history never masquerades as certainty.
 */
export function churnTier(explained: ExplainedHealth, history: HealthHistoryPoint[]): ChurnTierResult {
  const leadingIndicators: LeadingIndicator[] = [];

  const champion = driverBySignal(explained, 'champion');
  if (champion && champion.direction === 'down' && champion.deltaPts <= -6) {
    leadingIndicators.push('champion_departed');
  }
  const usage = driverBySignal(explained, 'usage');
  if (usage && usage.direction === 'down' && usage.deltaPts <= -3) {
    leadingIndicators.push('usage_down');
  }
  const support = driverBySignal(explained, 'support');
  if (support && support.direction === 'down' && support.deltaPts <= -3) {
    leadingIndicators.push('ticket_spike');
  }
  const renewal = driverBySignal(explained, 'renewal');
  const engagement = driverBySignal(explained, 'engagement');
  if (renewal && engagement && renewal.deltaPts <= -2 && engagement.deltaPts <= -2) {
    leadingIndicators.push('renewal_soon_no_exec_touch');
  }
  const billing = driverBySignal(explained, 'billing');
  if (billing && billing.deltaPts <= -4) {
    leadingIndicators.push('payment_overdue');
  }

  // Base risk from the explained score, boosted by named indicators and by
  // a genuinely declining history — trends predict, snapshots don't.
  let probability = ((100 - explained.score) / 100) * 0.6;
  for (const indicator of leadingIndicators) {
    probability += indicator === 'champion_departed' ? 0.2 : indicator === 'payment_overdue' ? 0.1 : 0.06;
  }
  const t = trend(history, 6);
  if (t.deltaPts < 0) probability += Math.min(0.15, Math.abs(t.deltaPts) / 100);
  probability = round2(clamp(probability, 0, 0.99));

  const tier: ChurnTierResult['tier'] =
    probability >= 0.7 ? 'critical' : probability >= 0.45 ? 'high' : probability >= 0.25 ? 'med' : 'low';

  const confidence = round2(
    clamp(0.35 + 0.1 * Math.min(history.length, CONFIDENCE_FULL_HISTORY), 0, 0.85),
  );

  return { tier, probability, confidence, leadingIndicators };
}

// ===========================================================================
// Signal derivation — 0–100 healthiness from the REAL record only
// ===========================================================================

function daysUntil(iso: string | undefined, now: number): number | undefined {
  if (!iso) return undefined;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return undefined;
  return Math.round((t - now) / DAY_MS);
}

/**
 * Derive the seven canonical signals (0–100 healthiness) from a customer's
 * real record — the same evidence health.ts debits, reshaped into signal
 * space so the UI can feed `explainableHealth` from already-loaded data.
 * Every mapping is a documented heuristic over record fields; nothing is
 * invented (e.g. usage is honestly an activity-volume proxy).
 */
export function deriveSignals(
  person: Person,
  input: { tasks?: ProjectTask[]; now?: number } = {},
): Record<string, number> {
  const now = input.now ?? Date.now();
  const tasks = input.tasks ?? [];

  // Engagement recency — silence is the loudest churn signal.
  const touch = lastTouchAt(person);
  const touchDays = touch ? Math.max(0, (now - Date.parse(touch)) / DAY_MS) : undefined;
  const engagement =
    touchDays === undefined ? 40 : touchDays <= 7 ? 95 : touchDays <= 30 ? 75 : touchDays <= 60 ? 50 : touchDays <= 90 ? 35 : 20;

  // Usage proxy — logged activity volume over the last 30 days.
  const recentActivity = (person.activity ?? []).filter(
    (a) => now - Date.parse(a.at) <= 30 * DAY_MS,
  ).length;
  const usage = recentActivity >= 6 ? 95 : recentActivity >= 3 ? 80 : recentActivity >= 1 ? 60 : 40;

  // Champion strength — a live primary POC that we actually talk to.
  const pocs = person.pocs ?? [];
  const champion =
    pocs.length === 0
      ? 55
      : touchDays !== undefined && touchDays <= 30
        ? 90
        : touchDays !== undefined && touchDays > 60
          ? 40
          : 65;

  // Support load — overdue open tasks tied to this account.
  const overdueTasks = tasks.filter((t) => {
    if (t.personId !== person.id || t.status === 'done') return false;
    const due = daysUntil(t.dueDate ?? undefined, now);
    return due !== undefined && due < 0;
  }).length;
  const support = clamp(85 - overdueTasks * 15, 20, 85);

  // Billing hygiene — overdue unpaid milestones drag it down hard.
  const overdueInvoices = (person.billingDates ?? []).filter((bd) => {
    const due = daysUntil(bd.date, now);
    return due !== undefined && due < 0 && (bd.status ?? 'scheduled') !== 'paid';
  }).length;
  const billing = clamp(90 - overdueInvoices * 30, 0, 90);

  // Renewal runway.
  const renewalDays = daysUntil(person.renewalDate, now);
  const renewal =
    renewalDays === undefined ? 60 : renewalDays < 0 ? 10 : renewalDays <= 14 ? 30 : renewalDays <= 30 ? 50 : 90;

  // Delivery health — blocked / overdue embedded deliverables.
  const blocked = (person.deliverables ?? []).filter((d) => d.status === 'blocked').length;
  const overdueDeliverables = (person.deliverables ?? []).filter((d) => {
    if (d.status === 'delivered') return false;
    const due = daysUntil(d.dueDate, now);
    return due !== undefined && due < 0;
  }).length;
  const delivery = clamp(90 - blocked * 25 - overdueDeliverables * 12, 10, 90);

  return { usage, engagement, champion, support, billing, renewal, delivery };
}
